import { apiService } from '../../services/ApiService';
import { showErrorToast } from './toast';

/**
 * THE UNDO for "create an asset so that something can hang off it".
 *
 * Two flows start on a bare model element and need an asset before they can do their job:
 * binding an IoT device (a device binds to an asset, never to a raw element) and raising a
 * ticket (so the ticket shows up under "Tickets for this asset", jumps to it in 3D and
 * appears in the diagram). Both create the asset FIRST, open their sheet, and — if the user
 * backs out without finishing — remove it again, so nobody is left holding an asset they
 * never asked for.
 *
 * One implementation, because the two flows must fail the same way: when the removal itself
 * fails, the asset is real and usable, and the person is TOLD so rather than being shown a
 * success that did not happen.
 *
 * Known limit, shared by both: the undo runs when the sheet is dismissed. Closing the tab or
 * refreshing while the sheet is open leaves the new asset in place.
 */
export type CreatedAssetPurpose = 'device' | 'ticket';

const FAILURE_MESSAGE: Record<CreatedAssetPurpose, string> = {
  device: 'The device was not added, but the new asset could not be removed',
  ticket: 'The ticket was not created, but the new asset could not be removed',
};

export async function rollbackCreatedAsset(
  siteId: string,
  poiId: number | string,
  purpose: CreatedAssetPurpose
): Promise<boolean> {
  try {
    await apiService.delete(`/assets/${siteId}/pois/batch-delete`, { poiIds: [poiId] });
    return true;
  } catch (err: any) {
    console.error(`[rollbackCreatedAsset] Failed to roll back the asset created for a ${purpose}:`, err);
    showErrorToast(FAILURE_MESSAGE[purpose]);
    return false;
  }
}

export default rollbackCreatedAsset;
